import React, { useEffect, useRef } from 'react';
import { oidcUserManager } from '../../services/oidcService';
import { clearSession } from '../../stores/session/clearSession';
import { getDesktopAssertion } from '../../services/desktopAssertion';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';

/**
 * Handles the redirect back from Identity Server after logout.
 * Clears all local auth state, then forces an explicit login prompt so the
 * identity server cannot silently re-authenticate via a surviving upstream session.
 *
 * A desktop session's logout (Header/UserPopover — PR #261 review) carries its last-known launch
 * assertion (stashed by DesktopAuth.tsx on every `/desktop?src=` entry, fast-path included) back through
 * the login redirect's `state`. Without it, `/signin-callback` cannot tell this apart from a plain
 * browser sign-in and routes to the AcombaGO tenant picker — which 404s into `/signin-error` for an
 * SSO account that only ever has Acomba-desktop access. No assertion (portal/browser sessions, or one
 * older than the stash's workday-scale TTL) falls through to the unchanged plain login prompt.
 */
export const SignoutCallback: React.FC = () => {
    const { clearUser } = useAuthStore();
    const { clearTenant } = useTenantStore();
    const called = useRef(false);

    useEffect(() => {
        if (called.current) return;
        called.current = true;

        void (async () => {
            try {
                await oidcUserManager.signoutRedirectCallback();
            } catch {
                // Non-critical — clear state regardless.
            } finally {
                clearSession();
                clearUser();
                clearTenant();
                try {
                    await oidcUserManager.removeUser();
                } catch (e) {
                    console.warn('removeUser failed — proceeding to login regardless', e);
                }
                const desktopAssertion = getDesktopAssertion();
                await oidcUserManager.signinRedirect(
                    desktopAssertion !== null
                        ? { prompt: 'login', max_age: 0, state: { src: desktopAssertion } }
                        : { prompt: 'login', max_age: 0 }
                );
            }
        })();
    }, [clearUser, clearTenant]);

    return (
        <div className="flex items-center justify-center min-h-screen">
            <p className="text-primary-600">Déconnexion en cours…</p>
        </div>
    );
};
